// Lógica pura de la ventana de conexión de Tiendanube: qué aviso llegó de `tn-connected.html`
// y si la persona está en el navegador de una app (Instagram, Facebook…). Sin red ni DOM,
// para probarla con vitest. Los efectos viven en `tiendaNube.ts`.
//
// El patrón (aviso por postMessage y, de respaldo, por localStorage) es el mismo que usa
// Mercado Libre, pero el código no se comparte a propósito: son integraciones separadas
// (ADR-2026-10-06-tiendanube-la-ventana-que-no-vuelve).

import { navegadorEmbebido } from './navegadorEmbebido';

/** Clave donde `tn-connected.html` deja el aviso por si el navegador perdió el `opener` (celulares). */
export const CLAVE_AVISO_TN = "atlas:tn-connected";

/**
 * Dónde queda el aviso de un intento: una clave por intento, para que dos conexiones que terminan
 * mientras Atlas está suspendido no se pisen (review Greptile #162). Sin id, la clave de siempre.
 */
export function claveDelAvisoTn(intentoId: string | null | undefined): string {
  return intentoId && /^[0-9a-f-]{36}$/i.test(intentoId) ? `${CLAVE_AVISO_TN}:${intentoId}` : CLAVE_AVISO_TN;
}

/** El `type` que manda `tn-connected.html`. */
export const TIPO_AVISO_TN = "tn-connected";

/** Un aviso guardado hace más que esto es de un intento viejo: se ignora. */
export const VIDA_DEL_AVISO_TN_MS = 15 * 60 * 1000;

/**
 * Cuánto espera Atlas a que vuelva la ventana de Tiendanube. Es lo que dura el link de
 * autorización (el state de `tn-connect` vence a los 10 min). Antes eran 3 minutos y se le
 * cerraba la ventana a quien tardaba en iniciar sesión.
 */
export const ESPERA_MAXIMA_TN_MS = 10 * 60 * 1000;

/** Lo que tira la espera cuando la persona cierra la ventana. Los botones lo comparan. */
export const MENSAJE_CONEXION_CANCELADA = "Conexión cancelada.";
/** Lo que tira la espera cuando se cumple `ESPERA_MAXIMA_TN_MS`. */
export const MENSAJE_TIEMPO_AGOTADO = "Se agotó el tiempo. Probá de nuevo.";

/** `intento`: el id que puso `tn-connect` en el state y que tn-callback devuelve en la URL. */
export type AvisoDeTiendanube = ({ ok: true } | { error: string }) & { intento?: string };

/**
 * ¿Este aviso es del intento que espera esta pestaña? Por localStorage (`estricto`) llega
 * cualquier aviso del navegador, también el de otra pestaña de Atlas que esté conectando a la
 * vez, así que tiene que traer el mismo id. Por postMessage llega sólo de la ventana propia:
 * ahí un aviso sin id (la página vieja) vale. Sin id propio (un `tn-connect` viejo) se acepta.
 */
export function esDeEsteIntento(
  aviso: AvisoDeTiendanube,
  intentoId: string | undefined,
  estricto: boolean,
): boolean {
  if (!intentoId) return true;
  if (!aviso.intento) return !estricto;
  return aviso.intento === intentoId;
}

/**
 * Lee lo que mandó `tn-connected.html` (por postMessage o por localStorage). Devuelve null si
 * no es un aviso de Tiendanube o si está vencido. Un aviso sin `t` (la página vieja, que sólo
 * avisaba por postMessage) vale.
 */
export function leerAvisoDeTiendanube(data: unknown, ahora = Date.now()): AvisoDeTiendanube | null {
  let d = data;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return null;
    }
  }
  if (!d || typeof d !== "object") return null;
  const a = d as { type?: unknown; error?: unknown; t?: unknown; intento?: unknown };
  if (a.type !== TIPO_AVISO_TN) return null;
  if (typeof a.t === "number" && ahora - a.t > VIDA_DEL_AVISO_TN_MS) return null;
  const intento =
    typeof a.intento === "string" && /^[0-9a-f-]{36}$/i.test(a.intento) ? { intento: a.intento } : {};
  if (typeof a.error === "string" && a.error.trim()) return { error: a.error.slice(0, 300), ...intento };
  return { ok: true, ...intento };
}

/**
 * Si la página corre dentro del navegador de una app, su nombre. Ahí la ventana de Tiendanube
 * no vuelve a Atlas (el 30/9 una persona probó desde Instagram en Android y recién conectó
 * desde la PC). Se detecta por las marcas que esas apps agregan al user agent.
 */
export function navegadorDentroDeApp(userAgent: string | null | undefined): string | null {
  // La regla vive en `navegadorEmbebido`, compartida con el aviso de Google del registro
  // (ADR-2026-10-20-google-avisa-en-instagram-sin-frenar). Un webview de Android sin nombre
  // devuelve null: estos mensajes nombran la app.
  return navegadorEmbebido(userAgent)?.app ?? null;
}

/** El mensaje cuando la ventana no se pudo abrir: si es por estar dentro de una app, se lo dice. */
export function mensajeDeVentanaBloqueada(userAgent: string | null | undefined): string {
  const app = navegadorDentroDeApp(userAgent);
  return app
    ? `Estás usando el navegador de ${app}. Abrí Atlas en Chrome o Safari para conectar tu tienda.`
    : "Permití las ventanas emergentes para conectar tu tienda.";
}

/** Cómo anotar en el registro un intento que terminó sin volver de Tiendanube, según el error de la espera. */
export function motivoDeCierre(mensaje: string): "ventana_cerrada" | "tiempo_agotado" | null {
  if (mensaje === MENSAJE_CONEXION_CANCELADA) return "ventana_cerrada";
  if (mensaje === MENSAJE_TIEMPO_AGOTADO) return "tiempo_agotado";
  return null;
}
